// useChartExport.js
import { useRef, useState } from "react";
import ExcelJS from "exceljs";

// If you also do "copy image" or other advanced logic, you can import them here.
// For brevity, I've included only PNG & Excel exports, but you can add the rest.

export function useChartExport({
  // optional arguments:
  defaultFileName = "chart_export", 
  onAlert = () => {},       // callback for success/error messages
  chartData = [],           // data used in Excel export
  headers = [],             // column headers for Excel or CSV
  customExcelLogic = null,  // optional function if you have custom Excel needs
  prepareDataForClipboard = null,  // optional custom data-building function
}) {
  // We'll store a ref to the chart container
  const chartRef = useRef(null);

  // Example: if you want to track suppressed groups or other meta
  const [suppressedGroups, setSuppressedGroups] = useState([]);

   /** 1) Copy Image to Clipboard */
   const copyImageToClipboard = async () => {
    if (!chartRef.current) {
      onAlert("Chart is not available for copying.", "error");
      return;
    }
    if (!("ClipboardItem" in window)) {
      onAlert("ClipboardItem is not supported in this browser.", "error");
      return;
    }

    // Find the SVG
    const svg = chartRef.current.querySelector("svg");
    if (!svg) {
      onAlert("SVG element not found.", "error");
      return;
    }

    try {
      // Serialize
      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(svg);

      // Ensure namespaces
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
        source = source.replace(
          /^<svg/,
          '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
        );
      }
      source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
      const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      // Create an image to load this URL
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            const scale = 2;
            const canvas = document.createElement("canvas");
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);

            // Turn canvas into blob
            const blob = await new Promise((resolveBlob, rejectBlob) => {
              canvas.toBlob((b) => {
                if (b) resolveBlob(b);
                else rejectBlob(new Error("Failed to generate image blob."));
              }, "image/png");
            });

            // Construct ClipboardItem
            const item = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([item]);

            URL.revokeObjectURL(url);
            onAlert("Chart image copied to clipboard!", "success");
            resolve();
          } catch (err) {
            onAlert("Error copying chart image to clipboard.", "error");
            reject(err);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          onAlert("Failed to load the chart image for copying.", "error");
          reject(new Error("Failed to load the chart image for copying."));
        };
        img.src = url;
      });
    } catch (error) {
      // Error copying chart image
      onAlert("Error copying chart image to clipboard.", "error");
    }
  };

  /** 2) Copy Data to Clipboard */
  const copyDataToClipboard = async () => {
    try {
      // If the chart needs a custom data format, call prepareDataForClipboard
      // otherwise do a simple fallback
      let text = "";
      if (prepareDataForClipboard) {
        text = prepareDataForClipboard(chartData);
      } else {
        // fallback: simple tab-delimited with headers
        if (headers.length > 0) {
          text += headers.join("\t") + "\n";
        }
        chartData.forEach((row) => {
          const rowValues = headers.map((h) =>
            row[h] != null ? row[h] : ""
          );
          text += rowValues.join("\t") + "\n";
        });
      }
      await navigator.clipboard.writeText(text);
      onAlert("Chart data copied to clipboard!", "success");
    } catch (err) {
      // Error copying data to clipboard
      onAlert("Error copying data to clipboard.", "error");
    }
  };


  // PNG export
  const exportChartAsPNG = () => {
    const container = chartRef.current;
    if (!container) {
      onAlert("Chart is not available for export.", "error");
      return;
    }
    const svg = container.querySelector("svg");
    if (!svg) {
      onAlert("SVG element not found.", "error");
      return;
    }

    try {
      // Serialize the SVG
      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(svg);

      // Namespaces
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
        source = source.replace(
          /^<svg/,
          '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
        );
      }

      // Add XML declaration
      source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
      const svgBlob = new Blob([source], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      // Create an <img> to load this URL
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const scale = 2;
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);

          // Create PNG data URL
          const pngData = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = pngData;
          link.download = `${defaultFileName}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          onAlert("Chart exported as PNG successfully!", "success");
        } catch (err) {
          onAlert("Error exporting chart as PNG.", "error");
          // Error exporting PNG
        }
      };
      img.onerror = () => {
        onAlert("Failed to load chart image for export.", "error");
        URL.revokeObjectURL(url);
      };
      img.src = url;
    } catch (error) {
      onAlert("Error exporting chart as PNG.", "error");
      // Error copying chart image
    }
  };

  // Excel export
  const exportChartToExcel = async () => {
    const container = chartRef.current;
    if (!container) {
      onAlert("Chart is not available for export.", "error");
      return;
    }
    const svg = container.querySelector("svg");
    if (!svg) {
      onAlert("SVG element not found.", "error");
      return;
    }

    try {
      // Serialize
      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(svg);
      if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      if (!source.match(/^<svg[^>]+"http:\/\/www\.w3\.org\/1999\/xlink"/)) {
        source = source.replace(
          /^<svg/,
          '<svg xmlns:xlink="http://www.w3.org/1999/xlink"'
        );
      }
      source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
      const svgBlob = new Blob([source], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = async () => {
        try {
          const scale = 2;
          const canvas = document.createElement("canvas");
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);

          const pngData = canvas.toDataURL("image/png");
          const resp = await fetch(pngData);
          const blob = await resp.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);

          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet("Chart Export");

          const imageId = workbook.addImage({
            buffer: uint8Array,
            extension: "png",
          });

          // Place the image in the sheet (example: A1)
          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 900, height: 400 },
          });

          if (customExcelLogic) {
            await customExcelLogic({
              workbook,
              worksheet,
              chartData,
              headers,
              tsvString: prepareDataForClipboard ? prepareDataForClipboard(chartData) : null,
            });
          } else {
            // Minimal example: write out data below row 25
            const startRow = 25;
            if (headers?.length) {
              headers.forEach((header, i) => {
                worksheet.getCell(`${String.fromCharCode(65 + i)}${startRow}`).value = header;
              });
              chartData.forEach((rowData, rowIdx) => {
                headers.forEach((header, colIdx) => {
                  worksheet.getCell(
                    `${String.fromCharCode(65 + colIdx)}${startRow + 1 + rowIdx}`
                  ).value = rowData[header] ?? null;
                });
              });
            }
          }

          const excelBuffer = await workbook.xlsx.writeBuffer();
          const excelBlob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const excelUrl = URL.createObjectURL(excelBlob);

          const link = document.createElement("a");
          link.href = excelUrl;
          link.download = `${defaultFileName}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(url);
          URL.revokeObjectURL(excelUrl);
          onAlert("Excel export complete!", "success");
        } catch (error) {
          onAlert("Error embedding chart into Excel.", "error");
          // Error copying chart image
        }
      };
      img.onerror = () => {
        onAlert("Failed to load chart image for export.", "error");
        URL.revokeObjectURL(url);
      };
      img.src = url;
    } catch (error) {
      onAlert("Error exporting chart to Excel.", "error");
      // Error copying chart image
    }
  };

  return {
    chartRef,               // attach to the <div> that wraps your chart
    exportChartAsPNG,
    exportChartToExcel,
    suppressedGroups,       // optional
    setSuppressedGroups,    // optional
    copyImageToClipboard,
    copyDataToClipboard,
    // ...any additional exports like copyImageToClipboard, copyDataToClipboard
  };
}